import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { AppThunk } from '../store';
import type { ConfirmContinuationState, ErrorResponse } from '../../types/confirmContinuation';
import axios from 'axios';
import amplifyConfig from '../../config';

const initialState: ConfirmContinuationState = {
  isLoading: false,
  hasError: false,
  serverMessage: undefined,
  isValid: undefined,
  lastDeleteDate: undefined,
};

const slice = createSlice({
  name: 'ConfirmContinuation',
  initialState,
  reducers: {
    startLoading(state: ConfirmContinuationState) {
      state.isLoading = true;
    },

    hasError(state: ConfirmContinuationState, action: PayloadAction<string>) {
      // console.log('message: ', action.payload);
      const message: string = action.payload;
      state.isLoading = false;
      state.hasError = true;
      state.serverMessage = message;
    },

    resetMessage(state: ConfirmContinuationState) {
      state.isLoading = false;
      state.hasError = false;
      state.serverMessage = undefined;
    },

    setServerMessage(state: ConfirmContinuationState, action: PayloadAction<string>): void {
      state.isLoading = false;
      state.serverMessage = action.payload;
    },

    setValidate(state: ConfirmContinuationState, action: PayloadAction<boolean>): void {
      state.isLoading = false;
      state.isValid = action.payload;
    },
  },
});

export const { reducer, actions } = slice;

export const resetMessage =
  (): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.resetMessage());
  };

export const validateSecret =
  (email: string, secret: string): AppThunk =>
  async (dispatch): Promise<void> => {
    dispatch(slice.actions.startLoading());

    try {
      // console.log('create params: ', params);
      const response = await axios.get<boolean>(amplifyConfig.apiGateway.URL + '/usercheck/validateSecret', {
        params: {
          email: email,
          secret: secret,
        },
        timeout: 60000,
      });

      if (response.data) {
        // valid secret
        dispatch(slice.actions.setValidate(response.data));
      } else {
        // invalid secret
        const message: string = 'MESSAGE_INVALID_SECRET';
        dispatch(slice.actions.setValidate(response.data));
        dispatch(slice.actions.setServerMessage(message));
      }
    } catch (err) {
      if (err.isAxiosError) {
        // err: AxiosError
        if (err.response) {
          const serverResponse: ErrorResponse = err.response.data;
          dispatch(slice.actions.hasError(serverResponse.message));
        } else {
          dispatch(slice.actions.hasError(err.message));
        }
      } else {
        // err: Error
        dispatch(slice.actions.hasError(err.message));
      }
    }
  };

export const postContinueUse =
  (email: string): AppThunk =>
  async (dispatch): Promise<void> => {
    dispatch(slice.actions.startLoading());

    try {
      const userEmail = email;
      const params = {
        email: userEmail,
      };
      // console.log('create params: ', params);
      const response = await axios.post(amplifyConfig.apiGateway.URL + '/usercheck/continueUse', params, {
        timeout: 60000,
      });

      const message: string = 'MESSAGE_SELECT_CONTINUATION';
      dispatch(slice.actions.resetMessage());
      dispatch(slice.actions.setServerMessage(message));
    } catch (err) {
      if (axios.isAxiosError(err)) {
        // err: AxiosError
        if (err.response) {
          const serverResponse: ErrorResponse = err.response.data;
          dispatch(slice.actions.hasError(serverResponse.message));
        } else {
          dispatch(slice.actions.hasError(err.message));
        }
      } else {
        // err: Error
        dispatch(slice.actions.hasError(err.message));
      }
    }
  };

export const postStopUse =
  (email: string): AppThunk =>
  async (dispatch): Promise<void> => {
    dispatch(slice.actions.startLoading());

    try {
      const userEmail = email;
      const params = {
        email: userEmail,
      };
      // console.log('create params: ', params);
      const response = await axios.post(amplifyConfig.apiGateway.URL + '/usercheck/stopUse', params, {
        timeout: 60000,
      });

      const message: string = 'MESSAGE_SELECT_DISCONTINUANCE';
      dispatch(slice.actions.resetMessage());
      dispatch(slice.actions.setServerMessage(message));
    } catch (err) {
      if (axios.isAxiosError(err)) {
        // err: AxiosError
        if (err.response) {
          const serverResponse: ErrorResponse = err.response.data;
          dispatch(slice.actions.hasError(serverResponse.message));
        } else {
          dispatch(slice.actions.hasError(err.message));
        }
      } else {
        // err: Error
        dispatch(slice.actions.hasError(err.message));
      }
    }
  };

export default slice;
